// Ledger categories — two lists (owner 2026-10-20; API migration 180): the IFRS list (global — today's list)
// and each country's own GAAP list, picked with the country buttons. One grid serves both. The 5 roots (Assets …
// Expenses) are shared by every list and read-only; a child category is edited in place at its row (golden
// rule 7) and added with the same form at the top of the list. A GAAP income or expense line also says where it
// sits on the income statement — its name is in its country's language, so the report cannot place it by its
// words — so one "Group" select sets both its root and that place.
import { html, signal, navigate } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { shell } from './shell.js';
import { pager, perPageSelect, DEFAULT_PER_PAGE } from '../shared/pager.js';
import { rowDelete } from '../shared/confirm.js';
import { flashId, keepScroll, revealRow } from '../shared/listnav.js';

const SIDE = { debit: 'success', credit: 'danger' };

// Where a GAAP line may sit on the income statement, by its root — the report's own section headings
// (reports.js). The API holds the same list (chart_rules.SECTIONS).
export const SECTIONS = {
  INCOME:  [{ v: 'sales', l: 'Sales' }, { v: 'other_income', l: 'Other Income' }],
  EXPENSE: [{ v: 'cos', l: 'Cost of Sales' }, { v: 'expenses', l: 'Expenses' }, { v: 'tax', l: 'Tax' }],
};
const sectionLabel = (rootCode, v) => ((SECTIONS[rootCode] || []).find(s => s.v === v) || {}).l || '';

// The Group choices: IFRS → the 5 roots; GAAP → a balance-sheet root, or an income / expense root with its
// place on the income statement. Value "rootId|section" (section empty for a balance-sheet root and for IFRS).
export function groupOptions(roots, standard){
  const out = [];
  for(const r of roots){
    const secs = standard === 'GAAP' ? (SECTIONS[r.code] || []) : [];
    if(!secs.length) out.push({ v: r.id + '|', l: r.name });
    else for(const s of secs) out.push({ v: r.id + '|' + s.v, l: r.name + ' · ' + s.l });
  }
  return out;
}

// One line on the picked country: its standard, its GAAP list and whether every ledger code has a GAAP line
// (a country can switch to GAAP only then — Countries › Edit country).
export function countryStatus(c){
  const parts = [c.name + ' uses ' + (c.accounting_standard || 'IFRS')];
  if(!c.gaap_categories) parts.push('no GAAP list yet');
  else {
    parts.push('its GAAP list has ' + c.gaap_categories + ' categor' + (c.gaap_categories === 1 ? 'y' : 'ies'));
    if(c.unmapped) parts.push(c.unmapped + ' of its ' + c.ledger_codes + ' ledger codes still need a GAAP category');
    else if(c.ledger_codes) parts.push('every ledger code has its GAAP category');
  }
  return parts.join(' · ');
}

export function categoriesScreen(standard = 'IFRS'){
  const gaap = standard === 'GAAP';
  const rows    = signal([]);
  const total   = signal(0);
  const page    = signal(1);
  const pages   = signal(1);
  const perPage = signal(DEFAULT_PER_PAGE);
  const loading = signal(true);
  const roots   = signal([]);     // the 5 shared roots — the Group select + each row's root
  const countries = signal(null); // GAAP: each country's standard and list sizes (/admin/accounting-standards); null = loading
  const country = signal(null);   // GAAP: the id of the country whose list is shown
  let timer, lastQ = '', sortBy = '', loadSeq = 0;

  // The one open form: null = none, 'new' = the add form at the top of the list, else the id of the row.
  const editId = signal(null);
  const fCode = signal(''), fName = signal(''), fGroup = signal(''), fSort = signal('');
  const formErr = signal(''), saving = signal(false), invalid = signal({});
  const confirmId = signal(null), deleting = signal(false), delErr = signal('');

  async function load(locateId){
    const seq = ++loadSeq;          // a slower earlier answer (another country, another page) never lands last
    loading.value = true;
    const params = new URLSearchParams({ per_page: perPage.value, page: String(page.value), standard });
    if(gaap){
      if(!country.value){ rows.value = []; total.value = 0; pages.value = 1; loading.value = false; return; }
      params.set('country_id', String(country.value));
    }
    if(lastQ && lastQ.trim()) params.set('q', lastQ.trim());
    if(sortBy) params.set('sort', sortBy);
    if(locateId) params.set('locate', String(locateId));
    let r = null;
    try { r = await api.get('/admin/ledger-categories?' + params.toString()); } catch { r = null; }
    if(seq !== loadSeq) return;
    rows.value = (r && r.data) || []; total.value = (r && r.total) || 0; pages.value = (r && r.total_pages) || 1;
    if(r && locateId && r.locate_page) page.value = r.locate_page;
    loading.value = false;
  }
  async function loadCountries(){
    try { countries.value = (await api.get('/admin/accounting-standards')).data || []; }
    catch { countries.value = []; }
  }
  if(gaap){
    (async () => {
      await loadCountries();
      const list = countries.value || [];
      const pick = list.find(c => c.gaap_categories > 0) || list[0];   // the first country with a GAAP list
      country.value = pick ? pick.id : null;
      load();
    })();
  } else load();
  (async () => { try { const r = await api.get('/admin/ledger-categories?per_page=200');
    roots.value = (r.data || []).filter(c => !c.parent_id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)); }
    catch {} })();

  function closeForm(){ editId.value = null; formErr.value = ''; invalid.value = {}; }
  function onSearch(e){ lastQ = e.target.value; page.value = 1; closeForm(); clearTimeout(timer); timer = setTimeout(load, 220); }
  function onPer(e){ perPage.value = e.target.value; page.value = 1; closeForm(); load(); }
  function onSort(e){ sortBy = e.target.value; page.value = 1; closeForm(); load(); }
  function goPage(p){ page.value = p; closeForm(); load(); }
  function pickCountry(id){
    if(id === country.value) return;
    country.value = id; page.value = 1;
    closeForm(); cancelDelete(); load();
  }

  // Focus a field of the form just opened, once the list has drawn it — after bringing the whole form into view
  // below the pinned app bar and page head. The add form opens at the top of the list, which sits under that
  // pinned head once the list is scrolled; focus() alone never scrolls there (the box counts as on screen).
  const focusField = f => setTimeout(() => {
    const card = document.querySelector('.lc-edit');
    if(!card) return;
    const pinned = Math.max(0, ...[...document.querySelectorAll('.app-bar, .page-head')]
      .filter(e => /^(sticky|fixed)$/.test(getComputedStyle(e).position))
      .map(e => e.getBoundingClientRect().bottom));
    const b = card.getBoundingClientRect(), gap = 8;
    if(b.top < pinned + gap) window.scrollBy(0, b.top - pinned - gap);
    else if(b.bottom > innerHeight - gap) window.scrollBy(0, Math.min(b.bottom - innerHeight + gap, b.top - pinned - gap));
    const el = card.querySelector('[data-f="' + f + '"]');
    if(el) el.focus({ preventScroll: true });
  }, 0);
  function openAdd(){
    if(gaap && !country.value) return;
    fCode.value = ''; fName.value = ''; fGroup.value = ''; fSort.value = '';
    formErr.value = ''; invalid.value = {}; cancelDelete(); editId.value = 'new';
    focusField('code');
  }
  function openEdit(c){
    if(!c.parent_id) return;                     // the shared roots are read-only
    fCode.value = c.code || ''; fName.value = c.name || '';
    fGroup.value = c.parent_id + '|' + (gaap ? (c.statement_section || '') : '');
    fSort.value = c.sort_order ?? '';
    formErr.value = ''; invalid.value = {}; cancelDelete(); editId.value = c.id;
    focusField('name');
  }
  // Typing clears that field's red border and the form's error line WITHOUT a signal write: the form lives in
  // the list's reactive block, and a re-render would rebuild the input under the caret (the focus trap). The
  // flag is cleared in place, so a later redraw does not paint the border back.
  const typed = (k, e) => {
    if(invalid.value[k]){ invalid.value[k] = false; e.target.classList.remove('is-invalid'); }
    const form = e.target.closest('.lc-edit');
    const line = form && form.querySelector('.row-err');
    if(line) line.remove();
  };
  async function save(e){
    if(e) e.preventDefault();
    formErr.value = '';                          // first: the same error must show again after typing removed it
    const inv = {};
    if(!fCode.value.trim())  inv.code  = true;
    if(!fName.value.trim())  inv.name  = true;
    if(!fGroup.value)        inv.group = true;
    invalid.value = inv;
    if(Object.keys(inv).length){ formErr.value = 'Please fill in the highlighted fields.'; return; }
    const [parentId, section] = fGroup.value.split('|');
    const editing = editId.value !== 'new';
    const id = editing ? editId.value : null;
    const body = { code: fCode.value.trim(), name: fName.value.trim(), parent_id: Number(parentId) };
    if(String(fSort.value).trim() !== '') body.sort_order = Number(fSort.value);   // empty keeps the order it has
    if(gaap) body.statement_section = section || null;
    if(!editing){ body.standard = standard; if(gaap) body.country_id = country.value; }
    saving.value = true;
    try {
      const res = editing ? await api.put('/admin/ledger-categories/' + id, body)
                          : await api.post('/admin/ledger-categories', body);
      closeForm();
      if(gaap) loadCountries();                  // the country's list size changed
      await (editing ? keepScroll(() => load(), id) : revealRow(load, res && res.id));
    } catch(err){ formErr.value = (err.data && err.data.error) || err.message || 'Save failed'; }
    saving.value = false;
  }

  function armDelete(id){ closeForm(); delErr.value = ''; confirmId.value = id; }
  function cancelDelete(){ confirmId.value = null; delErr.value = ''; }
  async function doDelete(id){
    deleting.value = true; delErr.value = '';
    try { await api.del('/admin/ledger-categories/' + id); confirmId.value = null; if(gaap) loadCountries(); await load(); }
    catch(err){ delErr.value = (err.data && err.data.error) || err.message || 'Delete failed'; }
    deleting.value = false;
  }

  // The add / edit form, drawn in place of the row (or at the top of the list to add). Plain values only — it
  // is part of the list's reactive block (CLAUDE.md: nested `${() => …}` slots append on re-render).
  const form = (c, opts, busy, err, inv) => html`
    <div class="row-card lc-edit">
      <form class="lc-edit-form" @submit=${save}>
        <label class="lc-fld lc-fld-code"><span class="lc-lbl">Code</span>
          <input class=${'form-control' + (inv.code ? ' is-invalid' : '')} data-f="code" maxlength="12"
                 .value=${fCode} @input=${e => { fCode.value = e.target.value; typed('code', e); }}
                 placeholder=${gaap ? 'e.g. TL1' : 'e.g. PPE'}></label>
        <label class="lc-fld lc-fld-name"><span class="lc-lbl">Name</span>
          <input class=${'form-control' + (inv.name ? ' is-invalid' : '')} data-f="name"
                 .value=${fName} @input=${e => { fName.value = e.target.value; typed('name', e); }}
                 placeholder=${gaap ? 'e.g. Liikevaihto' : 'e.g. Property, Plant & Equipment'}></label>
        <label class="lc-fld lc-fld-group"><span class="lc-lbl">${gaap ? 'Group' : 'IFRS root'}</span>
          <select class=${'form-control' + (inv.group ? ' is-invalid' : '')} data-f="group"
                  .value=${fGroup} @change=${e => { fGroup.value = e.target.value; typed('group', e); }}>
            <option value="">${gaap ? '— Select a group —' : '— Select an IFRS root —'}</option>
            ${opts.map(o => html`<option value=${o.v}>${o.l}</option>`)}
          </select></label>
        <label class="lc-fld lc-fld-sort"><span class="lc-lbl">Sort order</span>
          <input class="form-control" type="number" data-f="sort" .value=${fSort}
                 @input=${e => { fSort.value = e.target.value; }} placeholder="e.g. 110"></label>
        ${err ? html`<div class="row-err lc-edit-err">${err}</div>` : ''}
        <div class="lc-edit-actions">
          <button type="button" class="btn btn-outline btn-sm" @click=${closeForm}>Cancel</button>
          <button type="submit" class="btn btn-primary btn-sm" ?disabled=${busy}>${busy ? 'Saving…' : (c ? 'Save changes' : 'Add category')}</button>
        </div>
      </form>
    </div>`;

  const row = (c, rs, cId, del, dErr, hl) => {
    const isRoot = !c.parent_id;
    const root = isRoot ? c : rs.find(r => r.id === c.parent_id);
    const side = isRoot ? c.normal_side : (root && root.normal_side);
    const place = gaap && !isRoot ? sectionLabel(root && root.code, c.statement_section) : '';
    const sub = isRoot ? (gaap ? 'Root' : 'IFRS root') + ' · ' + (c.statement || '').replace(/_/g, ' ')
                       : (root ? root.name : '') + (place ? ' · ' + place : '');
    return html`
      <div class=${'row-card' + (isRoot ? ' is-root' : '') + (hl === c.id ? ' row-hl' : '')} @click=${() => openEdit(c)}>
        <span class="iso-badge">${c.code}</span>
        <span class="row-main"><b>${c.name}</b><span class="row-sub">${sub}</span></span>
        ${side ? html`<span class=${'badge badge-' + (SIDE[side] || 'secondary')}>${side}</span>` : ''}
        ${isRoot ? html`<span class="row-chevron"></span>`
                 : rowDelete({ armed: cId === c.id, busy: del, error: cId === c.id ? dErr : '',
                               onArm: () => armDelete(c.id), onCancel: cancelDelete, onDelete: () => doDelete(c.id) })}
        <span class="row-chevron">${isRoot ? '' : '✎'}</span>
      </div>`;
  };

  const list = () => {
    if(loading.value) return html`<div class="muted-block">Loading…</div>`;
    const eid = editId.value, busy = saving.value, err = formErr.value, inv = invalid.value;   // the open form
    const cId = confirmId.value, del = deleting.value, dErr = delErr.value;                  // delete state
    const hl = eid == null ? flashId.value : null;   // no flash redraw under an open form: it would take the caret
    const rs = roots.value, opts = groupOptions(rs, standard);
    const data = rows.value;
    const adding = eid === 'new' ? form(null, opts, busy, err, inv) : '';
    if(!data.length) return html`<div class="list-wrap"><div class="row-list">${adding}</div>
      <div class="muted-block">No categories match.</div></div>`;
    const pg = () => pager({ page: page.value, totalPages: pages.value, total: total.value,
                             perPage: Number(perPage.value), onPage: goPage });
    const none = gaap && !lastQ.trim() && !data.some(c => c.parent_id);
    // read only when the list is empty of GAAP lines: a list subscribed to `countries` would redraw (and take the
    // caret) when a save refreshes them
    const sel = none ? (countries.value || []).find(x => x.id === country.value) : null;
    return html`<div class="list-wrap">
      ${dErr ? html`<div class="alert alert-danger py-2 mb-2">${dErr}</div>` : null}
      ${pg()}
      <div class="row-list">${adding}${data.map(c => eid === c.id ? form(c, opts, busy, err, inv) : row(c, rs, cId, del, dErr, hl))}</div>
      ${none ? html`<div class="muted-block">No GAAP categories for ${sel ? sel.name : 'this country'} yet — add the first one.</div>` : ''}
      ${pg()}
    </div>`;
  };

  // GAAP: the country buttons, and one line on the picked country. A top-level slot of the static template.
  const chips = () => {
    const list = countries.value, sel = country.value;
    if(!list) return html`<div class="muted-block">Loading countries…</div>`;
    if(!list.length) return html`<div class="muted-block">No countries yet.</div>`;
    const c = list.find(x => x.id === sel);
    return html`
      <div class="lc-chips" role="group" aria-label="Country">
        ${list.map(x => html`<button type="button" class=${'chip-btn' + (x.id === sel ? ' chip-on' : '')}
            aria-pressed=${x.id === sel ? 'true' : 'false'} @click=${() => pickCountry(x.id)}>${x.name}${x.gaap_categories
              ? html`<span class="lc-chip-n">${x.gaap_categories}</span>` : ''}</button>`)}
      </div>
      ${c ? html`<div class="lc-status">${countryStatus(c)} ·
        <a class="lc-status-link" href=${'/countries/' + c.id}
           @click=${e => { e.preventDefault(); navigate('/countries/' + c.id); }}>Open ${c.name}</a></div>` : ''}`;
  };

  const content = html`
    <div class="page">
      <div class="page-head with-action">
        <div>
          <h1 class="page-title">${gaap ? 'Ledger categories · GAAP' : 'Ledger categories · IFRS'}</h1>
          <div class="page-sub">${gaap
            ? html`<span class="badge badge-primary">PER COUNTRY</span> Each country's own GAAP list — a company created in a GAAP country uses it`
            : html`<span class="badge badge-primary">GLOBAL</span> IFRS classification — applies to every IFRS country`}</div>
        </div>
        <button class="btn btn-primary" ?disabled=${() => gaap && !country.value} @click=${openAdd}>+ Add category</button>
      </div>
      ${gaap ? chips : ''}
      <div class="toolbar">
        <input class="form-control" type="search" placeholder="Search code or name…" @input=${onSearch}>
        <select class="form-control per-select" @change=${onSort}>
          <option value="">Custom order</option>
          <option value="code">Sort by code</option>
          <option value="name">Sort by name</option>
        </select>
        ${perPageSelect(perPage.value, onPer)}
      </div>
      <div class="count-line">${total} categories</div>
      ${list}
    </div>`;

  return shell(gaap ? 'categories-gaap' : 'categories', gaap ? 'Ledger categories · GAAP' : 'Ledger categories · IFRS', content);
}
